import React, { useState } from 'react';
import { SessionMode } from '../types';

interface Props {
  onConnected: (apiKey: string, mode: SessionMode, resumeSessionId?: string) => void;
  error?: string | null;
  initialApiKey?: string;
  initialMode?: SessionMode;
}

export function AgentConnect({ onConnected, error, initialApiKey, initialMode }: Props) {
  const [apiKey, setApiKey] = useState(initialApiKey || '');
  const [resumeSessionId, setResumeSessionId] = useState('');
  const [mode, setMode] = useState<SessionMode>(initialMode || SessionMode.Text);
  const [connecting, setConnecting] = useState(false);

  const handleConnect = async () => {
    if (!apiKey.trim()) return;
    setConnecting(true);
    try {
      onConnected(apiKey.trim(), mode, resumeSessionId.trim() || undefined);
    } finally {
      setConnecting(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.heading}>Connect to an agent</h2>
        <p style={styles.intro}>
          Paste an agent API key to start a live session. Voice mode lets you
          talk to the agent and hear spoken replies; text mode is a typed chat.
          You can find a key on the agent&apos;s page.
        </p>

        <label style={styles.label}>
          Agent API Key *
          <input
            style={styles.input}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="ag_..."
            autoFocus
          />
        </label>

        <label style={styles.label}>
          Conversation ID (optional — a session id or an /api/std thread id to continue)
          <input
            style={styles.input}
            value={resumeSessionId}
            onChange={(e) => setResumeSessionId(e.target.value)}
            placeholder="leave empty for a new conversation"
          />
        </label>

        <div style={styles.label}>
          Mode
          <div style={styles.modeToggle}>
            <button
              style={{
                ...styles.modeBtn,
                ...(mode === SessionMode.Text ? styles.modeBtnActive : {}),
              }}
              onClick={() => setMode(SessionMode.Text)}
            >
              Text
            </button>
            <button
              style={{
                ...styles.modeBtn,
                ...(mode === SessionMode.Voice ? styles.modeBtnActive : {}),
              }}
              onClick={() => setMode(SessionMode.Voice)}
            >
              Voice
            </button>
          </div>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <button
          style={{ ...styles.connectBtn, opacity: !apiKey.trim() || connecting ? 0.6 : 1 }}
          onClick={handleConnect}
          disabled={!apiKey.trim() || connecting}
        >
          {connecting ? 'Connecting...' : 'Connect'}
        </button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  card: {
    width: '100%', maxWidth: 420, padding: '24px 28px',
    border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff',
    display: 'flex', flexDirection: 'column', gap: 14,
  },
  heading: { margin: 0, fontSize: 18, color: '#1a1a1a' },
  intro: { margin: 0, fontSize: 13, lineHeight: 1.5, color: '#666' },
  label: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#555' },
  input: { padding: '10px 14px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, fontFamily: 'monospace', outline: 'none' },
  modeToggle: { display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid #ddd' },
  modeBtn: { flex: 1, padding: '8px 16px', border: 'none', background: '#f9fafb', fontSize: 13, fontWeight: 500, cursor: 'pointer', color: '#666' },
  modeBtnActive: { background: '#4f46e5', color: '#fff' },
  error: { padding: '8px 12px', background: '#fff0f0', border: '1px solid #fcc', borderRadius: 8, color: '#c00', fontSize: 13 },
  connectBtn: { padding: '10px 20px', borderRadius: 8, border: 'none', background: '#4f46e5', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
};
